import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody, Button } from 'reactstrap';
import BreadCrumb from '../components/Common/BreadCrumb';
import SortableVerticalList from '../components/Common/SortableVerticalList';
import { homeLayoutService } from '../services';
import { HOME_SECTION_KEYS, STATIC_HOME_SECTION_LABELS } from '../constants/homeSections';
import { toast } from 'react-toastify';

const HomeSectionOrderPage: React.FC = () => {
  const [order, setOrder] = useState<string[]>([]);
  const [sectionLabels, setSectionLabels] = useState<Record<string, string>>(STATIC_HOME_SECTION_LABELS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { sectionOrder, sectionLabels: labels } = await homeLayoutService.getSectionOrder();
      setOrder(sectionOrder.length ? sectionOrder : [...HOME_SECTION_KEYS]);
      setSectionLabels({ ...STATIC_HOME_SECTION_LABELS, ...labels });
    } catch {
      setOrder([...HOME_SECTION_KEYS]);
      setSectionLabels(STATIC_HOME_SECTION_LABELS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const labelFor = (key: string) => sectionLabels[key] ?? STATIC_HOME_SECTION_LABELS[key] ?? key;

  const handleSave = async () => {
    if (order.length !== HOME_SECTION_KEYS.length) {
      toast.error('Section list is incomplete. Reload the page.');
      return;
    }
    setSaving(true);
    try {
      await homeLayoutService.updateSectionOrder(order);
      toast.success('Home section order saved. The app Home API will use this order.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefault = () => {
    setOrder([...HOME_SECTION_KEYS]);
  };

  return (
    <>
      <BreadCrumb pageTitle="Home section order" title="Home section order" />
      <Card>
        <CardBody>
          <h5 className="card-title mb-2">Drag and drop to reorder</h5>
          <p className="text-muted small mb-3">
            This order matches the top-level key order of <code>GET /user/home</code> (JSON object key order). All
            sections must stay in the list (no hiding). Category-backed sections (e.g. Webinars) show the parent
            category name; keys stay fixed for the mobile app.
          </p>
          {loading ? (
            <p className="text-muted mb-0">Loading…</p>
          ) : (
            <>
              <div className="mb-3">
                <SortableVerticalList
                  itemIds={order}
                  onReorder={setOrder}
                  renderRow={(key) => (
                    <>
                      <span className="fw-medium">{labelFor(key)}</span>
                      <code className="small text-muted ms-auto">{key}</code>
                    </>
                  )}
                />
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button color="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save order'}
                </Button>
                <Button color="secondary" outline onClick={handleResetDefault} disabled={saving} type="button">
                  Reset to default
                </Button>
                <Button color="link" className="text-decoration-none" onClick={load} disabled={saving} type="button">
                  Reload from server
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default HomeSectionOrderPage;
